// iter220 — Frontend mirror of backend listing_description_policy.py
// Backend is source of truth; this file gives users instant feedback
// while typing/submitting without waiting for a round-trip.

export const DESCRIPTION_MAX_LEN = 3000;

// Extensible competitor list — MUST stay in sync with backend
// COMPETITOR_PLATFORMS in listing_description_policy.py.
export const COMPETITOR_PLATFORMS = [
  "حراج",
  "haraj",
  "السوق المفتوح",
  "opensooq",
  "open sooq",
  "مستعمل",
  "syarah",
];
const GENERIC_PLATFORM_WORDS = new Set(["سيارة"]);

// ── Regexes ───────────────────────────────────────────────────────
const PHONE_RE = /(?:(?:\+|00)?966[\s\-.]?)?0?5[\s\-.]?(?:\d[\s\-.]?){7,9}\d/u;
const EMAIL_RE = /[A-Za-z0-9._%+\-]+\s*@\s*[A-Za-z0-9.\-]+\s*\.\s*[A-Za-z]{2,}/;
const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"']+|\b[a-z0-9\-]{2,}\.(?:com|net|org|sa|io|co|me|ai|app|shop|store)(?:\/[^\s<>"']*)?/i;
const REPEAT_SYMBOL_RE = /([-_=.~*ـ#+•·—–])\1{7,}/;
const EMPTY_LINES_RE = /\n{3,}/;

const stripDiacritics = (s) => s.normalize("NFKD").replace(/\p{M}/gu, "");
const normalizeForPlatformMatch = (s) =>
  stripDiacritics((s || "").toLowerCase())
    .replace(/ـ/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Validate a listing description. Returns:
 *   { ok: true }                     — description is acceptable
 *   { ok: false, code, message_ar }  — description is rejected
 * Empty / whitespace-only input is always ok.
 */
export function validateDescription(raw) {
  const text = (raw || "").trim();
  if (!text) return { ok: true };

  if (text.length > DESCRIPTION_MAX_LEN) {
    return {
      ok: false,
      code: "description_too_long",
      message_ar: `وصل وصف الإعلان إلى الحد الأقصى المسموح به وهو ${DESCRIPTION_MAX_LEN} حرف.`,
      message_en: `Description exceeds the ${DESCRIPTION_MAX_LEN}-character limit.`,
    };
  }
  if (PHONE_RE.test(text)) {
    return {
      ok: false,
      code: "description_contains_phone",
      message_ar: "لا يُسمح بإضافة أرقام التواصل داخل وصف الإعلان. استخدم وسائل التواصل المتاحة في الإعلان.",
      message_en: "Phone numbers are not allowed inside the description. Use the ad's contact options.",
    };
  }
  if (EMAIL_RE.test(text)) {
    return {
      ok: false,
      code: "description_contains_email",
      message_ar: "لا يُسمح بإضافة البريد الإلكتروني داخل وصف الإعلان. استخدم وسائل التواصل المتاحة في الإعلان.",
      message_en: "Email addresses are not allowed inside the description. Use the ad's contact options.",
    };
  }
  if (URL_RE.test(text)) {
    return {
      ok: false,
      code: "description_contains_url",
      message_ar: "لا يُسمح بإضافة روابط خارجية داخل وصف الإعلان.",
      message_en: "External links are not allowed inside the description.",
    };
  }
  const normalized = normalizeForPlatformMatch(text);
  for (const name of COMPETITOR_PLATFORMS) {
    if (GENERIC_PLATFORM_WORDS.has(name)) continue;
    const needle = normalizeForPlatformMatch(name);
    if (needle && normalized.includes(needle)) {
      return {
        ok: false,
        code: "description_mentions_competitor",
        message_ar: "يرجى تعديل وصف الإعلان. لا يُسمح بذكر منصات أو مواقع خارجية داخل وصف الإعلان.",
        message_en: "Please edit the description. Mentioning competing platforms is not allowed.",
      };
    }
  }
  if (REPEAT_SYMBOL_RE.test(text) || EMPTY_LINES_RE.test(text)) {
    return {
      ok: false,
      code: "description_formatting_abuse",
      message_ar: "يرجى تبسيط تنسيق الوصف — تجنّب تكرار الشرطات أو الرموز أو الأسطر الفارغة.",
      message_en: "Please simplify the description formatting — avoid long runs of dashes, symbols, or blank lines.",
    };
  }
  return { ok: true };
}
